import { LitElement, html, css } from 'lit';

class MetalyxNav extends LitElement {
  static properties = {
    scrolled: { type: Boolean, reflect: true },
    _menuOpen: { type: Boolean, state: true },
  };

  static styles = css`
    :host {
      display: block;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 100;
      transition: background var(--transition, 200ms ease),
                  box-shadow var(--transition, 200ms ease);
    }
    :host([scrolled]) {
      background: rgba(10, 14, 26, 0.95);
      backdrop-filter: blur(12px);
      box-shadow: 0 1px 0 var(--color-border, rgba(255,255,255,0.06));
    }

    nav {
      max-width: var(--max-width, 1200px);
      margin: 0 auto;
      padding: 1rem var(--space-container, 1.5rem);
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .logo {
      display: inline-flex;
      align-items: center;
      gap: 0.6rem;
      text-decoration: none;
    }
    .logo-icon {
      width: 32px;
      height: 32px;
      flex-shrink: 0;
    }
    .logo-text {
      font-family: var(--font-heading, 'Outfit', sans-serif);
      font-size: var(--text-xl, 1.25rem);
      font-weight: 800;
      color: var(--color-text-heading, #fff);
      letter-spacing: 0.08em;
    }

    .nav-links {
      display: flex;
      align-items: center;
      gap: 2rem;
      list-style: none;
      margin: 0;
      padding: 0;
    }

    .nav-links a {
      font-family: var(--font-sans, sans-serif);
      font-size: var(--text-sm, 0.875rem);
      font-weight: 500;
      color: var(--color-text-muted, #64748B);
      text-decoration: none;
      transition: color var(--transition, 200ms ease);
      letter-spacing: 0.01em;
    }
    .nav-links a:hover {
      color: var(--color-text-heading, #fff);
    }

    .nav-cta {
      font-size: var(--text-sm, 0.875rem) !important;
      font-weight: 600 !important;
      color: var(--color-metalyx-light, #C4B5FD) !important;
      padding: 0.5rem 1.25rem;
      border: 1px solid rgba(124, 58, 237, 0.3);
      border-radius: var(--radius-md, 0.75rem);
      transition: all var(--transition, 200ms ease) !important;
      background: rgba(124, 58, 237, 0.05);
    }
    .nav-cta:hover {
      background: var(--color-accent-glow, rgba(124, 58, 237, 0.15)) !important;
      color: #DDD6FE !important;
      border-color: rgba(124, 58, 237, 0.5);
    }

    .hamburger {
      display: none;
      background: none;
      border: none;
      color: var(--color-text-primary, #CBD5E1);
      cursor: pointer;
      padding: 0.25rem;
    }

    .mobile-menu {
      display: none;
    }

    @media (max-width: 1024px) {
      .nav-links {
        display: none;
      }
      .hamburger {
        display: flex;
        align-items: center;
      }
      .mobile-menu {
        display: none;
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(10, 14, 26, 0.98);
        backdrop-filter: blur(16px);
        z-index: 200;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 2rem;
      }
      .mobile-menu.open {
        display: flex;
      }
      .mobile-menu a {
        font-family: var(--font-heading, 'Outfit', sans-serif);
        font-size: var(--text-2xl, 1.5rem);
        font-weight: 600;
        color: var(--color-text-primary, #CBD5E1);
        text-decoration: none;
        transition: color var(--transition, 200ms ease);
      }
      .mobile-menu a:hover {
        color: var(--color-accent, #7C3AED);
      }
      .close-btn {
        position: absolute;
        top: 1rem;
        right: var(--space-container, 1.5rem);
        background: none;
        border: none;
        color: var(--color-text-primary, #CBD5E1);
        cursor: pointer;
      }
    }
  `;

  constructor() {
    super();
    this.scrolled = false;
    this._menuOpen = false;
  }

  _toggleMenu() {
    this._menuOpen = !this._menuOpen;
  }

  _closeMenu() {
    this._menuOpen = false;
  }

  _openContact(e) {
    e.preventDefault();
    this._menuOpen = false;
    window.dispatchEvent(new CustomEvent('open-contact'));
  }

  _scrollTo(id, e) {
    e.preventDefault();
    this._menuOpen = false;
    const el = document.querySelector('app-shell')?.shadowRoot?.querySelector(`metalyx-page`)?.shadowRoot?.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }

  render() {
    return html`
      <nav>
        <a class="logo" href="#">
          <svg class="logo-icon" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="nav-oTopL" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stop-color="#7C3AED"/>
                <stop offset="100%" stop-color="#6D28D9"/>
              </linearGradient>
              <linearGradient id="nav-oTopR" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stop-color="#6D28D9"/>
                <stop offset="100%" stop-color="#5B21B6"/>
              </linearGradient>
              <linearGradient id="nav-iTop" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stop-color="#DDD6FE"/>
                <stop offset="100%" stop-color="#C4B5FD"/>
              </linearGradient>
            </defs>
            <polygon points="256,80 416,168 256,256" fill="url(#nav-oTopR)"/>
            <polygon points="256,80 96,168 256,256" fill="url(#nav-oTopL)"/>
            <polygon points="96,168 256,256 96,344" fill="#4C1D95"/>
            <polygon points="256,256 256,432 96,344" fill="#3B0764"/>
            <polygon points="416,168 256,256 416,344" fill="#6D28D9"/>
            <polygon points="256,256 256,432 416,344" fill="#5B21B6"/>
            <polygon points="256,155 336,199 256,243 176,199" fill="url(#nav-iTop)"/>
            <polygon points="176,199 256,243 256,331 176,287" fill="#A78BFA"/>
            <polygon points="336,199 256,243 256,331 336,287" fill="#C4B5FD"/>
          </svg>
          <span class="logo-text">METALYX</span>
        </a>
        <ul class="nav-links">
          <li><a href="#mt-features" @click="${(e) => this._scrollTo('#mt-features', e)}">Features</a></li>
          <li><a href="#mt-arch" @click="${(e) => this._scrollTo('#mt-arch', e)}">Platform</a></li>
          <li><a href="#mt-benefits" @click="${(e) => this._scrollTo('#mt-benefits', e)}">Benefits</a></li>
          <li><a class="nav-cta" href="#" @click="${this._openContact}">Request a Demo</a></li>
        </ul>
        <button class="hamburger" @click="${this._toggleMenu}" aria-label="Toggle menu">
          <metalyx-icon name="menu" size="28"></metalyx-icon>
        </button>
      </nav>

      <div class="mobile-menu ${this._menuOpen ? 'open' : ''}">
        <button class="close-btn" @click="${this._closeMenu}" aria-label="Close menu">
          <metalyx-icon name="close" size="32"></metalyx-icon>
        </button>
        <a href="#mt-features" @click="${(e) => this._scrollTo('#mt-features', e)}">Features</a>
        <a href="#mt-arch" @click="${(e) => this._scrollTo('#mt-arch', e)}">Platform</a>
        <a href="#mt-benefits" @click="${(e) => this._scrollTo('#mt-benefits', e)}">Benefits</a>
        <a href="#" @click="${this._openContact}">Contact</a>
      </div>
    `;
  }
}

customElements.define('metalyx-nav', MetalyxNav);